/**
 * Local-only placeholder id for an empty chat thread. Not sent to the API.
 * @returns {string} UUID
 */
export const createLocalSessionId = () => crypto.randomUUID();

/**
 * Get stored session ID from localStorage for a user
 * @param {string} [userId] - User ID for per-user key. If falsy, returns null.
 * @returns {string|null} Session ID or null
 */
export const getStoredSessionId = (userId) => {
  if (!userId) return null;
  return localStorage.getItem(`sessionId-${userId}`) || null;
};

/**
 * Store session ID in localStorage for a user
 * @param {string} sessionId - Session ID to store
 * @param {string} [userId] - User ID for per-user key. If falsy, no-op.
 */
export const storeSessionId = (sessionId, userId) => {
  if (!userId) return;
  localStorage.setItem(`sessionId-${userId}`, sessionId);
};

/**
 * Store sessions data in localStorage for a user
 * @param {Object} sessions - Sessions object to store
 * @param {string} [userId] - User ID for per-user key. If falsy, no-op.
 */
export const storeSessions = (sessions, userId) => {
  if (!userId) return;
  localStorage.setItem(`chatSessions-${userId}`, JSON.stringify(sessions));
};

/**
 * Get stored sessions from localStorage for a user
 * @param {string} [userId] - User ID for per-user key. If falsy, returns {}.
 * @returns {Object} Sessions object or empty object if none found
 */
export const getStoredSessions = (userId) => {
  if (!userId) return {};
  const stored = localStorage.getItem(`chatSessions-${userId}`);
  if (!stored) return {};
  try {
    return JSON.parse(stored);
  } catch (error) {
    console.warn('Failed to parse stored sessions:', error);
    return {};
  }
};

/**
 * Clear all application data from localStorage (preserves chat history)
 * Clears: revetall-*, supabase.* keys
 * Does NOT clear: sessionId-*, chatSessions-* (chat history is preserved)
 */
export const clearAllAppData = () => {
  const keysToRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && (key.startsWith('revetall-') || key.startsWith('supabase.'))) {
      keysToRemove.push(key);
    }
  }

  keysToRemove.forEach((k) => localStorage.removeItem(k));
};
